import { useEffect, useRef, useState } from 'react';
import { useTonConnectModal, useTonWallet } from '@tonconnect/ui-react';

import { type TONTransactionRequestDataType } from '../types/query/user';

import { useTonTransaction } from './ton-connect-transaction';

// type RequestFunction = () => Promise<ActivateVipStatusResponseType>;

type TransactionCallbacks = {
  onSuccess?: () => void;
  onError?: () => void;
};

interface UseTonAutoConnectReturnType {
  <T extends TONTransactionRequestDataType>(
    sendRequest: () => Promise<T>,
    callbacks?: TransactionCallbacks,
  ): Promise<void>;
}

interface SendRequestAfterWalletConnectType {
  <T extends TONTransactionRequestDataType>(
    sendRequest: () => Promise<T>,
  ): Promise<void>;
}

interface SendRequestType {
  <T extends TONTransactionRequestDataType>(): Promise<T>;
}

const useTonAutoConnect = (): UseTonAutoConnectReturnType => {
  const wallet = useTonWallet();
  const { open } = useTonConnectModal();
  const [waitingWalletConnection, setWaitingWalletConnection] = useState(false);
  const { sendTONTransaction } = useTonTransaction();
  const sendRequestRef = useRef<SendRequestType | null>(null);
  // const transactionDataRef = useRef<TONTransactionRequestDataType | null>(null);
  const callbacksRef = useRef<TransactionCallbacks | undefined>();

  const connectWalletAndDoTransaction = async <
    T extends TONTransactionRequestDataType,
  >(
    // transactionData: TONTransactionRequestDataType,
    sendRequest: () => Promise<T>,
    callbacks?: TransactionCallbacks,
  ): Promise<void> => {
    try {
      if (wallet) {
        const { address, amount, payload, validUntil } = await sendRequest();
        await sendTONTransaction(
          { address, amount, payload, validUntil },
          callbacks,
        );
      } else {
        setWaitingWalletConnection(true);
        sendRequestRef.current = sendRequest as SendRequestType;
        // transactionDataRef.current = transactionData;
        callbacksRef.current = callbacks;
        open();
      }
    } catch (error) {
      console.error(error);
      callbacks?.onError?.();
    }
  };

  useEffect(() => {
    const sendRequestAfterWalletConnect: SendRequestAfterWalletConnectType =
      async (sendRequest) => {
        try {
          const { address, amount, payload, validUntil } = await sendRequest();
          await sendTONTransaction(
            { address, amount, payload, validUntil },
            callbacksRef.current,
          );
        } catch (error) {
          console.error(error);
          callbacksRef.current?.onError?.();
        } finally {
          setWaitingWalletConnection(false);
          sendRequestRef.current = null;
          callbacksRef.current = undefined;
        }
      };

    if (wallet && waitingWalletConnection && sendRequestRef.current) {
      sendRequestAfterWalletConnect(sendRequestRef.current);
    }
  }, [wallet, waitingWalletConnection]);

  return connectWalletAndDoTransaction;
};

export { useTonAutoConnect };
